import { useSelector } from '@tunasong/redux'
import {
  Button,
  fillCopy,
  RequestAccessButton,
  requestAccessCopy,
  Typography,
  useLocation,
  useNavigate,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useRef } from 'react'
import { Browser } from 'react-kawaii'
import styles from './entity-access-denied.module.css'

export interface EntityAccessDeniedProps {
  /** The denied item. With it, Request access is offered; the response never carries the item's data. */
  entityId?: string
  /** The caller turned out to have access (for example, granted meanwhile): the host refetches */
  onHasAccess?: () => void
}

/**
 * Shown when an entity cannot be loaded because the user may not see it (GH #1731).
 *
 * No "Try again": a permission denial does not change on retry. Request access is the primary action when the
 * item is known; the request response never carries the item's data (request-access spec §6.1, §8.3). The owner
 * is not named because on this path it is not known: the entity read is what was denied.
 */
export const EntityAccessDenied: FC<EntityAccessDeniedProps> = props => {
  const { entityId, onHasAccess } = props
  const navigate = useNavigate()
  const location = useLocation()
  const email = useSelector(state => state.user.email)
  const headingRef = useRef<HTMLElement>(null)

  /** Move focus to the heading so screen readers announce the state, not the page they left. */
  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  /**
   * Only offer "Go back" for an in-app previous entry. `history.length` also counts other sites and the
   * sign-in redirect, so a shared link opened from chat or email would send the user out of the app.
   * React Router keys the first entry of a session 'default'.
   */
  const canGoBack = location.key !== 'default'
  const canRequest = Boolean(entityId)

  const body =
    canRequest && email
      ? fillCopy(requestAccessCopy.deniedBody, { email })
      : email
        ? `You're signed in as ${email}. If someone shared it with you, ask them to share it with this address.`
        : 'If someone shared it with you, ask them to give you access.'

  const goBack = (
    <Button onClick={() => void navigate(-1)} variant={canRequest ? 'text' : 'contained'}>
      Go back
    </Button>
  )
  const goHome = (
    <Button onClick={() => void navigate('/app')} variant={canGoBack || canRequest ? 'text' : 'contained'}>
      Go home
    </Button>
  )

  return (
    <section className={styles.container} aria-labelledby="entity-access-denied-heading">
      <div aria-hidden="true">
        <Browser size={160} mood="sad" color={'var(--tuna-palette-secondary-main)'} />
      </div>
      <Typography
        id="entity-access-denied-heading"
        variant="h5"
        ref={headingRef}
        tabIndex={-1}
        className={styles.heading}
      >
        You don&apos;t have access to this
      </Typography>
      <Typography variant="body1" color="textSecondary" className={styles.body}>
        {body}
      </Typography>
      <div className={styles.actions}>
        {entityId ? (
          <>
            {/* Requesting is the only forward action, so it is primary; one secondary way out (§8.3) */}
            <RequestAccessButton entityId={entityId} onHasAccess={onHasAccess} />
            {canGoBack ? goBack : goHome}
          </>
        ) : (
          <>
            {canGoBack ? goBack : null}
            {goHome}
          </>
        )}
      </div>
    </section>
  )
}
